<template>
  <div class="agent-memory-manager">
    <div class="memory-header">
      <h4>{{ agent.name }} Memory</h4>
      <p>Upload and manage files for this agent's memory system</p>
    </div>

    <!-- Memory Status -->
    <div class="memory-status">
      <div class="status-item">
        <span class="label">Memory Status:</span>
        <span class="value" :class="{ 'enabled': agent.settings?.enable_memory }">
          {{ agent.settings?.enable_memory ? 'Enabled' : 'Disabled' }}
        </span>
      </div>
      <div v-if="!agent.settings?.enable_memory" class="memory-warning">
        <i class="icon-warning"></i>
        Memory is disabled for this agent. Enable it in agent settings to use memory features.
      </div>
    </div>

    <!-- Upload Section -->
    <div v-if="agent.settings?.enable_memory" class="upload-section">
      <h5>Upload Memory File</h5>
      <div class="upload-form">
        <!-- Source Toggle: Upload vs Knowledge Base -->
        <div class="form-group">
          <label>Add by:</label>
          <div class="toggle-group">
            <label class="radio-option">
              <input type="radio" value="file" v-model="addSource" />
              <span>Upload Memory File</span>
            </label>
            <label class="radio-option">
              <input type="radio" value="kb" v-model="addSource" />
              <span>Select from knowledge base</span>
            </label>
          </div>
        </div>

        <!-- File Upload Fields -->
        <template v-if="addSource === 'file'">
          <div class="form-group">
            <label for="memory-file-input">Select File:</label>
            <input 
              id="memory-file-input"
              type="file" 
              @change="handleFileSelect"
              class="file-input"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="memory-type">Memory Type:</label>
              <select v-model="uploadForm.type" id="memory-type" class="form-control">
                <option value="">Select type...</option>
                <option value="document">Document</option>
                <option value="image">Image</option>
                <option value="text">Text</option>
                <option value="reference">Reference</option>
              </select>
            </div>

            <div class="form-group">
              <label for="memory-title">Title (Optional):</label>
              <input 
                v-model="uploadForm.title" 
                id="memory-title"
                type="text" 
                class="form-control"
                placeholder="Enter a descriptive title"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="memory-tags">Tags (Optional):</label>
            <input 
              v-model="uploadForm.tags" 
              id="memory-tags"
              type="text" 
              class="form-control"
              placeholder="comma-separated tags (e.g., strategy, trading, finance)"
            />
          </div>
        </template>

        <!-- Knowledge Base Selection -->
        <template v-else>
          <div class="form-group">
            <label for="kb-select">Knowledge Base</label>
            <select id="kb-select" v-model="selectedKnowledgeBaseId" class="form-control">
              <option value="">Select a knowledge base...</option>
              <option v-for="kb in knowledgeBases" :key="kb.id" :value="kb.id">{{ kb.name }}</option>
            </select>
            <div class="hint" v-if="kbLoading">Loading knowledge bases...</div>
            <div class="error" v-if="kbError">{{ kbError }}</div>
          </div>

          <!-- Knowledge Base Documents -->
          <div v-if="selectedKnowledgeBaseId" class="kb-docs">
            <div class="form-group">
              <label>Documents</label>
              <div v-if="kbDocsLoading" class="hint">Loading documents...</div>
              <div v-else-if="kbDocsError" class="error">{{ kbDocsError }}</div>
              <div v-else>
                <div v-if="kbDocuments.length === 0" class="hint">No documents found for this knowledge base.</div>
                <div v-else class="checkbox-list">
                  <label class="checkbox-item" v-for="doc in kbDocuments" :key="doc.id">
                    <input type="checkbox" :value="doc.id" v-model="selectedDocumentIds" />
                    <span>{{ getDocDisplayName(doc) }}</span>
                  </label>
                </div>
                <div v-if="kbDocuments.length > 0 && selectedDocumentIds.length === 0" class="validation-error">
                  Please select at least one document.
                </div>

                <!-- Actions -->
                <div class="kb-actions">
                  <button type="button" class="btn btn-secondary" @click="clearSelectedDocs" :disabled="selectedDocumentIds.length === 0 || kbDocsSubmitting">
                    Clear
                  </button>
                  <button type="button" class="btn btn-primary" @click="submitSelectedDocs" :disabled="selectedDocumentIds.length === 0 || kbDocsSubmitting">
                    <span v-if="kbDocsSubmitting">Submitting...</span>
                    <span v-else>Submit</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <button 
          v-if="addSource === 'file'"
          @click="uploadFile" 
          :disabled="!canUpload || uploading"
          class="btn btn-primary upload-btn"
        >
          <span v-if="uploading">Uploading...</span>
          <span v-else><i class="icon-upload"></i> Upload to Agent Memory</span>
        </button>
      </div>
    </div


    <!-- File List Section -->
    <div v-if="agent.settings?.enable_memory" class="files-section">
      <div class="section-header">
        <h5>Agent Memory Files</h5>
        <div class="header-right">
          <div class="source-toggle" role="tablist" aria-label="Memory source">
            <button 
              type="button" 
              class="toggle-btn" 
              :class="{ active: filesSource === 'agent' }" 
              @click="filesSource = 'agent'"
            >
              Agent
            </button>
            <button 
              type="button" 
              class="toggle-btn" 
              :class="{ active: filesSource === 'kb' }" 
              @click="filesSource = 'kb'"
            >
              Knowledge Base
            </button>
          </div>
          <div class="filter-controls">
            <select v-model="filterType" @change="loadFiles" class="filter-select">
              <option value="">All Types</option>
              <option value="document">Documents</option>
              <option value="image">Images</option>
              <option value="text">Text</option>
              <option value="reference">References</option>
            </select>
            <button @click="loadFiles" class="btn btn-sm btn-secondary">
              <i class="icon-refresh"></i> Refresh
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading">Loading agent memory files...</div>
      
      <div v-else-if="displayFiles.length === 0" class="no-files">
        <i class="icon-memory"></i>
        <p>No memory files found for this selection.</p>
        <p>Upload files to enhance your agent's knowledge base.</p>
      </div>

      <div v-else class="files-grid">
        <div v-for="file in displayFiles" :key="file.id" class="file-card">
          <div class="file-icon">
            <i :class="getFileIcon(file.content_type)"></i>
          </div>
          <div class="file-info">
            <h6 class="file-name">{{ file.title || file.name }}</h6>
            <p class="file-details">
              {{ formatFileSize(file.size) }} • {{ file.type }} • {{ formatDate(file.upload_date) }}
            </p>
            <div v-if="file.tags && file.tags.length" class="file-tags">
              <span v-for="tag in file.tags" :key="tag" class="tag">{{ tag }}</span>
            </div>
          </div>
          <div class="file-actions">
            <button @click="downloadFile(file)" class="btn btn-sm btn-outline" title="Download">
              <i class="icon-download"></i>
            </button>
            <button @click="deleteFile(file)" class="btn btn-sm btn-danger" title="Delete">
              <i class="icon-delete"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { API_ENDPOINTS, getApiUrl } from '@/config/api'

export default {
  name: 'AgentMemoryManager',
  props: {
    agent: {
      type: Object,
      required: true
    }
  },
  emits: ['upload-success', 'error'],
  setup(props, { emit }) {
    const files = ref([])
    const loading = ref(false)
    const uploading = ref(false)
    const filterType = ref('')

    // Files source toggle: 'agent' | 'kb'
    const filesSource = ref('agent')

    // Add-by source: 'file' or 'kb'
    const addSource = ref('file')

    // Knowledge base selection state
    const knowledgeBases = ref([])
    const selectedKnowledgeBaseId = ref('')
    const kbLoading = ref(false)
    const kbError = ref('')

    // KB documents state
    const kbDocuments = ref([])
    const kbDocsLoading = ref(false)
    const kbDocsError = ref('')
    const selectedDocumentIds = ref([])
    const kbDocsSubmitting = ref(false)

    const loadKnowledgeBases = async () => {
      kbLoading.value = true
      kbError.value = ''
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(getApiUrl(API_ENDPOINTS.knowledgeBases.list), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        if (!response.ok) {
          throw new Error('Failed to load knowledge bases')
        }
        const data = await response.json()
        if (Array.isArray(data)) {
          knowledgeBases.value = data
        } else if (Array.isArray(data.items)) {
          knowledgeBases.value = data.items
        } else {
          knowledgeBases.value = []
        }
      } catch (e) {
        console.error('Error loading knowledge bases:', e)
        kbError.value = e?.message || 'Failed to load knowledge bases'
        knowledgeBases.value = []
      } finally {
        kbLoading.value = false
      }
    }

    const loadKnowledgeBaseDocuments = async (kbId) => {
      if (!kbId) {
        kbDocuments.value = []
        return
      }
      kbDocsLoading.value = true
      kbDocsError.value = ''
      selectedDocumentIds.value = []
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(getApiUrl(API_ENDPOINTS.knowledgeBases.documents(kbId)), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        if (!response.ok) {
          throw new Error('Failed to load documents')
        }
        const data = await response.json()
        if (Array.isArray(data)) {
          kbDocuments.value = data
        } else if (Array.isArray(data.items)) {
          kbDocuments.value = data.items
        } else {
          kbDocuments.value = []
        }
      } catch (e) {
        console.error('Error loading KB documents:', e)
        kbDocsError.value = e?.message || 'Failed to load documents'
        kbDocuments.value = []
      } finally {
        kbDocsLoading.value = false
      }
    }

    // Load KBs when switching to KB mode
    watch(addSource, (val) => {
      if (val === 'kb' && knowledgeBases.value.length === 0 && !kbLoading.value) {
        loadKnowledgeBases()
      }
    })

    // Load documents when a knowledge base is selected
    watch(selectedKnowledgeBaseId, (id) => {
      if (id) {
        loadKnowledgeBaseDocuments(id)
      } else {
        kbDocuments.value = []
        selectedDocumentIds.value = []
        kbDocsError.value = ''
      }
    })
    
    
    const uploadForm = ref({
      file: null,
      type: '',
      title: '',
      tags: ''
    })

    const canUpload = computed(() => {
      return uploadForm.value.file && props.agent.settings?.enable_memory
    })

    const loadFiles = async () => {
      if (!props.agent.settings?.enable_memory) return
      
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        const url = new URL(getApiUrl(API_ENDPOINTS.memory.agentFiles(props.agent.id)), window.location.origin)
        
        if (filterType.value) {
          url.searchParams.append('type', filterType.value)
        }

        const response = await fetch(url.toString(), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          files.value = Array.isArray(data) ? data : []
        } else {
          console.error('Failed to load files:', response.statusText)
          files.value = []
          emit('error', 'Failed to load memory files')
        }
      } catch (error) {
        console.error('Error loading files:', error)
        files.value = []
        emit('error', 'Error loading memory files')
      } finally {
        loading.value = false
      }
    }

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) {
        uploadForm.value.file = file
        
        // Auto-detect file type
        if (file.type.startsWith('image/')) {
          uploadForm.value.type = 'image'
        } else if (file.type.includes('pdf') || file.type.includes('document') || file.type.includes('text')) {
          uploadForm.value.type = 'document'
        } else if (file.type.includes('text')) {
          uploadForm.value.type = 'text'
        }
      }
    }

    const uploadFile = async () => {
      if (!canUpload.value) return

      uploading.value = true
      try {
        const token = localStorage.getItem('token')
        const formData = new FormData()
        
        formData.append('file', uploadForm.value.file)
        if (uploadForm.value.type) {
          formData.append('type', uploadForm.value.type)
        }
        if (uploadForm.value.title) {
          formData.append('title', uploadForm.value.title)
        }
        
        if (uploadForm.value.tags) {
          formData.append('tags', uploadForm.value.tags)
        }

        // Build URL with agent metadata as query parameters
        const url = new URL(getApiUrl(API_ENDPOINTS.memory.upload), window.location.origin)
        if (props.agent?.id) url.searchParams.append('meta.agent_id', props.agent.id)
        if (props.agent?.name) url.searchParams.append('meta.agent_name', props.agent.name)

        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        })

        if (response.ok) {
          emit('upload-success', 'File uploaded to agent memory successfully!')
          resetUploadForm()
          loadFiles()
        } else {
          const error = await response.json()
          emit('error', error.error || 'Upload failed')
        }
      } catch (error) {
        console.error('Upload error:', error)
        emit('error', 'Upload failed')
      } finally {
        uploading.value = false
      }
    }

    const downloadFile = async (file) => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(getApiUrl(API_ENDPOINTS.memory.download(file.id)), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = file.name
          a.click()
          window.URL.revokeObjectURL(url)
        } else {
          emit('error', 'Download failed')
        }
      } catch (error) {
        console.error('Download error:', error)
        emit('error', 'Download failed')
      }
    }

    const deleteFile = async (file) => {
      if (!confirm(`Are you sure you want to delete \"${file.name}\" from ${props.agent.name}'s memory?`)) return

      try {
        const token = localStorage.getItem('token')
        const storage = String(file.storage || '').toLowerCase()
        let url
        if (storage === 'local') {
          // Delete local knowledge base files using the new endpoint with agentId and file name
          const fileName = encodeURIComponent(file.name || file.title || '')
          url = getApiUrl(`/api/memory/agent/files/${props.agent.id}/local/${fileName}`)
        } else {
          // Default deletion by file id (e.g., google_drive or others)
          url = getApiUrl(API_ENDPOINTS.memory.delete(file.id))
        }

        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          emit('upload-success', 'File deleted from agent memory')
          loadFiles()
        } else {
          let msg = 'Delete failed'
          try {
            const err = await response.json()
            msg = err.error || err.message || msg
          } catch {}
          emit('error', msg)
        }
      } catch (error) {
        console.error('Delete error:', error)
        emit('error', 'Delete failed')
      }
    }
    

    
    const resetUploadForm = () => {
      uploadForm.value = {
        file: null,
        type: '',
        title: '',
        tags: ''
      }
      const fileInput = document.getElementById('memory-file-input')
      if (fileInput) fileInput.value = ''
    }

    const getFileIcon = (contentType) => {
      if (contentType.includes('pdf')) return 'icon-file-pdf'
      if (contentType.includes('image')) return 'icon-file-image'
      if (contentType.includes('text')) return 'icon-file-text'
      if (contentType.includes('document')) return 'icon-file-document'
      return 'icon-file'
    }

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString()
    }

    // Filter files by selected source and type
    const displayFiles = computed(() => {
      let list = Array.isArray(files.value) ? files.value : []
      // Prefer origin field when classifying files; fallback to storage for backward compatibility
      list = list.filter(f => {
        const origin = String(f.origin || '').toLowerCase()
        const storage = String(f.storage || '').toLowerCase()
        if (filesSource.value === 'agent') {
          // Agent list: origin === 'Agent-Files'
          if (origin) return origin === 'agent-files'
          // Fallback: non-local storage treated as agent files
          return ['google_drive', 's3'].includes(storage)
        }
        if (filesSource.value === 'kb') {
          // Knowledge base list: origin === 'Agent-Knowledge-Base'
          if (origin) return origin === 'agent-knowledge-base'
          // Fallback: local storage treated as knowledge base
          return storage === 'local'
        }
        return true
      })
      if (filterType.value) {
        list = list.filter(f => String(f.type || '') === filterType.value)
      }
      return list
    })

    // Best-effort display name for KB documents
    const getDocDisplayName = (doc) => {
      if (!doc || typeof doc !== 'object') return 'Untitled document'
      return (
        doc.title ||
        doc.name ||
        doc.filename ||
        doc.file_name ||
        doc.original_name ||
        doc.file?.name ||
        doc.metadata?.filename ||
        doc.metadata?.original_name ||
        `Document ${doc.id || ''}`.trim()
      )
    }

    const clearSelectedDocs = () => {
      selectedDocumentIds.value = []
    }

    const submitSelectedDocs = async () => {
      if (!selectedKnowledgeBaseId.value || selectedDocumentIds.value.length === 0 || kbDocsSubmitting.value) return
      const selectedDocs = kbDocuments.value.filter(d => selectedDocumentIds.value.includes(d.id))
      const payload = {
        agent_id: props.agent?.id || null,
        agent_name: props.agent?.name || '',
        knowledge_base_id: selectedKnowledgeBaseId.value,
        documents: selectedDocs.map(d => ({ id: d.id, name: getDocDisplayName(d) }))
      }

      kbDocsSubmitting.value = true
      try {
        const token = localStorage.getItem('token')
        const resp = await fetch(getApiUrl('/api/knowledge-bases/documents/pull-local'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        })
        if (!resp.ok) {
          let errText = 'Failed to submit selected documents'
          try {
            const err = await resp.json()
            errText = err.error || err.message || errText
          } catch {}
          throw new Error(errText)
        }
      } catch (e) {
        console.error(e)
      } finally {
        kbDocsSubmitting.value = false
      }
    }

    // Editing helpers
    const initialZohoSnapshot = ref(null)
    const startEditingZoho = () => {
      initialZohoSnapshot.value = {
        clientId: zohoClientId.value,
        clientSecret: zohoClientSecret.value,
        orgId: zohoBookOrgId.value,
        crm: integrateZohoCrm.value,
        book: integrateZohoBook.value
      }
      zohoMessage.value = ''
      zohoSuccess.value = false
      editingZoho.value = true
    }

    const cancelEditingZoho = () => {
      if (initialZohoSnapshot.value) {
        zohoClientId.value = initialZohoSnapshot.value.clientId || ''
        zohoClientSecret.value = initialZohoSnapshot.value.clientSecret || ''
        zohoBookOrgId.value = initialZohoSnapshot.value.orgId || ''
        integrateZohoCrm.value = !!initialZohoSnapshot.value.crm
        integrateZohoBook.value = !!initialZohoSnapshot.value.book
      } else if (hasZohoConfig.value) {
        applyZohoConfig()
      }
      zohoMessage.value = ''
      zohoSuccess.value = false
      editingZoho.value = false
    }


    // Watch for agent changes
    watch(() => props.agent.id, () => {
      loadFiles()
    }, { immediate: true })

    onMounted(() => {
      loadFiles()
    })

    return {
      files,
      loading,
      uploading,
      filterType,
      uploadForm,
      canUpload,
      loadFiles,
      handleFileSelect,
      uploadFile,
      downloadFile,
      deleteFile,
      getFileIcon,
      formatFileSize,
      formatDate,
      getDocDisplayName,
      // Files source and filtered list
      filesSource,
      displayFiles,
      // New KB/Source bindings
      addSource,
      knowledgeBases,
      selectedKnowledgeBaseId,
      kbLoading,
      kbError,
      // KB documents
      kbDocuments,
      kbDocsLoading,
      kbDocsError,
      selectedDocumentIds,
      clearSelectedDocs,
      submitSelectedDocs,
      kbDocsSubmitting,
      
    }
  }
}
</script>

<style scoped>
.agent-memory-manager {
  padding: 1.5rem;
  height: 100%;
  overflow-y: auto;
}

.memory-header {
  margin-bottom: 2rem;
}

.memory-header h4 {
  margin: 0 0 0.5rem;
  color: #2c3e50;
  font-size: 1.5rem;
}

.memory-header p {
  margin: 0;
  color: #6c757d;
  font-size: 0.95rem;
}

.memory-status {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-item .label {
  font-weight: 600;
  color: #495057;
}

.status-item .value {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  background: #ffebee;
  color: #c62828;
}

.status-item .value.enabled {
  background: #e8f5e8;
  color: #2e7d32;
}

.memory-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #fff3cd;
  color: #856404;
  border-radius: 6px;
  font-size: 0.9rem;
}

.upload-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.upload-section h5 {
  margin: 0 0 1rem;
  color: #2c3e50;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.form-control, .file-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
}

.form-control:focus, .file-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.upload-btn {
  width: 100%;
  margin-top: 1rem;
}

/* Removed Zoho styles */

.action-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

.zoho-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.zoho-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 2px solid #e9ecef;
  border-radius: 4px;
  background: #ffffff;
  position: relative;
  cursor: pointer;
}

.zoho-checkbox input[type="checkbox"]:checked {
  background: #667eea;
  border-color: #667eea;
}

.zoho-checkbox input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
}

.zoho-checkbox input[type="checkbox"]:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.zoho-message {
  margin-top: 0.75rem;
  font-size: 0.9rem;
}

.zoho-message.success {
  color: #2e7d32;
}

.zoho-message.error {
  color: #c62828;
}

.files-section {
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.source-toggle {
  display: inline-flex;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 999px;
  padding: 2px;
}

.toggle-btn {
  border: none;
  background: transparent;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.85rem;
  color: #6c757d;
  cursor: pointer;
}

.toggle-btn.active {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.section-header h5 {
  margin: 0;
  color: #2c3e50;
}

.filter-controls {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.filter-select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85rem;
}

.loading, .no-files {
  padding: 3rem;
  text-align: center;
  color: #6c757d;
}

.no-files i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.files-grid {
  padding: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.file-card {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  background: #f8f9fa;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.file-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.file-icon {
  text-align: center;
}

.file-icon i {
  font-size: 2rem;
  color: #007bff;
}

.file-info {
  flex: 1;
}

.file-name {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
}

.file-details {
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  color: #6c757d;
}

.file-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.tag {
  background: #007bff;
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-size: 0.7rem;
}

.file-actions {
  display: flex;
  gap: 0.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s;
  font-weight: 500;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.8rem;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.btn-outline {
  background: white;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-outline:hover {
  background: #007bff;
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

/* KB docs styles */
.kb-docs {
  margin-top: 0.75rem;
}
.checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}
.checkbox-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 2px solid #e9ecef;
  border-radius: 4px;
  background: #ffffff;
  position: relative;
}
.checkbox-item input[type="checkbox"]:checked {
  background: #667eea;
  border-color: #667eea;
}
.checkbox-item input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -56%);
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
}
.validation-error {
  color: #c62828;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.kb-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

/* Icons */
.icon-warning:before { content: '⚠️'; }
.icon-upload:before { content: '⬆️'; }
.icon-refresh:before { content: '🔄'; }
.icon-memory:before { content: '🧠'; }
.icon-download:before { content: '⬇️'; }
.icon-delete:before { content: '🗑️'; }
.icon-file:before { content: '📄'; }
.icon-file-pdf:before { content: '📕'; }
.icon-file-image:before { content: '🖼️'; }
.icon-file-text:before { content: '📝'; }
.icon-file-document:before { content: '📋'; }

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .files-grid {
    grid-template-columns: 1fr;
  }
  
  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .filter-controls {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
